import { layoutRoute } from '@app/router/layout';
import {
  createRoute, Navigate, Outlet, useRouterState
} from '@tanstack/react-router';
import SubmitApplications from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications';
import CaseCreationLayout from '@modules/opportunities/presentation/pages/CaseCreation';
import CaseSubmissionBasicInfo from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo';
import CaseCreationDocuments from '@modules/opportunities/presentation/pages/CaseCreation/views/DocumentSubmission';
import CaseCreationBankForms from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/BankForms';
import CaseSubmissionFillBankForms from '@modules/opportunities/presentation/pages/CaseCreation/views/FillBankForms';
import { NotFoundComponent } from '@modules/core/components/NotFoundComponent';
import { BankApplicationKanban } from '@modules/opportunities/presentation/pages/BankApplicationsKanban';
import { VaultForm } from '@modules/opportunities/presentation/pages/CaseCreation/views/FillBankForms/VaultForm';
import BankProductsSelectionPage from '@modules//opportunities/presentation/pages/CaseCreation/views/BankSubmission/BankProductsSelection';
import { CasesOverviewV2 } from '@modules//opportunities/presentation/pages/Cases/List';

export type AdditionalDocumentsSearchQuery = {
  clientId?: string;
};

export const CasesBackwardCompatibleRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/case/*',
  beforeLoad: () => ({ featureName: 'opportunities.module' }),
  component: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useRouterState();
    const redirectTo = ctx.location.pathname.replace('/case', '/cases');
    return <Navigate to={ redirectTo } replace />;
  },
});

export const CasesLayoutRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/cases',
  beforeLoad: () => ({ featureName: 'opportunities.module' }),
  component: () => <Outlet />,
});

export type CasesRouteSearchQuery = {
  q?: string;
  page?: number;
  status?: string;
  submitted_by?: string;
  createCase?: string;
  order_by?: string,
  order?: string,
  opportunity_id?: string,
  bank_application_id?: string,
};

export const CasesRoute = createRoute({
  getParentRoute: () => CasesLayoutRoute,
  path: '/',
  beforeLoad: () => ({ breadcrumb: { key: 'caseListing' } }),
  component: () => (
    <CasesOverviewV2 />
  ),
  validateSearch: ({
    q, page, status, submitted_by, createCase, order_by, order,
  }: CasesRouteSearchQuery): CasesRouteSearchQuery => (
    {
      q,
      page: page || 1,
      status,
      submitted_by,
      createCase,
      order_by,
      order,
    }
  ),
});

export type CasesSearchQuery = {
  // Start Date Range
  start_date?: string;
  // End Date Range
  end_date?: string;
  status?: string;
  submitted_by?: string;
  order?: string;
  sort?: string;
  page?: number;
  page_size?: string;
  searchable_name?: string;
};

export const BankApplicationsKanbanRoute = createRoute({
  getParentRoute: () => CasesLayoutRoute,
  path: '/kanban',
  beforeLoad: () => ({ breadcrumb: { key: 'caseListing' } }),
  component: () => (
    <BankApplicationKanban />
  ),
  validateSearch: (search: Record<keyof CasesSearchQuery, any>): CasesSearchQuery => ({
    start_date: search.start_date || '',
    end_date: search.end_date || '',
    status: search.status || '',
    submitted_by: search.submitted_by || '',
    sort: search.sort || '',
    order: search.order || '',
    page: search.page || 1,
    page_size: search.page_size || 1,
    searchable_name: search.searchable_name || '',
  }),
});

type CaseSubmissionBankDocumentsRouteSearchParams = {
  applicant: 'main' | 'additional';
};

export const CaseCreationLayoutRoute = createRoute({
  getParentRoute: () => CasesLayoutRoute,
  path: '/$opportunityId',
  beforeLoad: () => ({ breadcrumb: { key: 'caseListing.createCase' } }),
  component: () => (
    <CaseCreationLayout>
      <Outlet />
    </CaseCreationLayout>
  ),
  notFoundComponent: () => <NotFoundComponent />,
});

export const CaseCreationBasicInfoRoute = createRoute({
  getParentRoute: () => CaseCreationLayoutRoute,
  path: '/basic-info',
  component: () => <CaseSubmissionBasicInfo />,
});

export const CaseCreationBankDocumentsRoute = createRoute({
  getParentRoute: () => CaseCreationLayoutRoute,
  path: '/documents',
  component: () => <CaseCreationDocuments />,
  validateSearch: (
    { applicant }: CaseSubmissionBankDocumentsRouteSearchParams
  ): CaseSubmissionBankDocumentsRouteSearchParams =>
    ({ applicant: applicant || '' }
    ),
});

export const CaseCreationFillFormsRoute = createRoute({
  getParentRoute: () => CaseCreationLayoutRoute,
  path: '/bank-fields',
  component: () => <Outlet />,
});

export const CaseCreationVaultFillFormsRoute = createRoute({
  getParentRoute: () => CaseCreationFillFormsRoute,
  path: '/requested-from-vault',
  component: () => <VaultForm />,
});
export const CaseCreationBanksFillFormsRoute = createRoute({
  getParentRoute: () => CaseCreationFillFormsRoute,
  path: '/remaining-fields',
  component: () => <CaseSubmissionFillBankForms />,
});

export const CaseCreationSubmissionRootRoute = createRoute({
  getParentRoute: () => CaseCreationLayoutRoute,
  path: '/submission',
  component: () => <Outlet />,
});

export const CaseCreationSelectBankProductsRoute = createRoute({
  getParentRoute: () => CaseCreationSubmissionRootRoute,
  path: '/bank-products',
  component: () => <BankProductsSelectionPage />,
});

export const CaseCreationBankFormsRoute = createRoute({
  getParentRoute: () => CaseCreationSubmissionRootRoute,
  path: '/bank-forms',
  component: () => <CaseCreationBankForms />,
});

export const CaseCreationSubmitApplicationsRoute = createRoute({
  getParentRoute: () => CaseCreationSubmissionRootRoute,
  path: '/submit',
  component: () => <SubmitApplications />,
  validateSearch: ({ bank_application_id }: {
    bank_application_id: string;
  }): { bank_application_id: string; } => ({ bank_application_id: bank_application_id || '' }),
});
